/*
  menara-presentoir.js — présentoirs sculpturaux de la page d'accueil
  (« Les tableaux, en mouvement. », « Des coussins qui ont du caractère. »).

  Un seul moteur pour les deux sections. Chaque <menara-presentoir> est
  autonome : ses écouteurs, ses minuteurs et ses boucles d'animation ne
  débordent jamais sur l'autre présentoir.

  Principe du mouvement
  ---------------------
  Une position continue « p », exprimée en crans, suit une cible entière au
  moyen d'un ressort à amortissement critique : départ en douceur, long
  ralentissement à l'arrivée, jamais de rebond. Chaque produit se place
  d'après son écart à p, interpolé entre des positions clés lues dans le CSS
  (centre, voisins, extérieurs). Le glissement écrit p directement ; au
  relâcher, le ressort repart avec la vitesse du geste. Il n'y a donc aucune
  rupture entre le doigt, les flèches et l'animation.

  Coût
  ----
  Pendant le mouvement, seuls transform et opacity sont écrits, et
  uniquement lorsqu'ils changent. Les mesures (largeur de scène, variables
  CSS) sont lues une fois, puis à chaque redimensionnement. Hors écran, rien
  ne tourne : ni avance automatique, ni parallaxe.
*/

const MOUVEMENT_REDUIT = window.matchMedia('(prefers-reduced-motion: reduce)');
const POINTEUR_FIN = window.matchMedia('(hover: hover) and (pointer: fine)');

const borne = (v, min, max) => (v < min ? min : v > max ? max : v);
const modulo = (a, n) => ((a % n) + n) % n;

/** Interpolation par morceaux entre les crans 0, 1, 2 et 3. */
function palier(cles, a) {
  if (a >= 3) return cles[3] + (cles[3] - cles[2]) * (a - 3);
  const i = Math.floor(a);
  return cles[i] + (cles[i + 1] - cles[i]) * (a - i);
}

/** Position des repères sur l'arc de l'indicateur (0,12 → 0,88 de sa largeur). */
const repere = (i, n) => (n < 2 ? 0.5 : 0.12 + (0.76 * i) / (n - 1));

class MenaraPresentoir extends HTMLElement {
  connectedCallback() {
    if (this.moteurActif) return;
    this.moteurActif = true;
    this.demarrer();
  }

  disconnectedCallback() {
    this.arreter();
    this.moteurActif = false;
  }

  get calme() {
    return !this.animations || MOUVEMENT_REDUIT.matches;
  }

  get omega() {
    // Le ressort atteint 95 % du trajet au bout de la durée réglée.
    return 4.75 / (this.duree / 1000);
  }

  demarrer() {
    this.nettoyeurs = [];
    this.items = Array.from(this.querySelectorAll('[data-mnp-item]'));
    this.n = this.items.length;
    this.scene = this.querySelector('[data-mnp-scene]');
    if (!this.n || !this.scene) return;

    const d = this.dataset;
    // En dessous de cinq produits, boucler ferait apparaître le même
    // produit des deux côtés à la fois.
    this.boucle = this.n >= 5;
    this.design = Boolean(window.Shopify && window.Shopify.designMode);
    this.duree = borne(parseInt(d.duree, 10) || 700, 300, 1500);
    this.animations = d.animations !== 'false';
    this.auto = d.auto === 'true' && !this.design;
    this.delaiAuto = borne(parseInt(d.autoDelai, 10) || 5000, 2000, 15000);

    const depart = borne(parseInt(d.depart, 10) || 0, 0, this.n - 1);
    this.p = depart;
    this.cible = depart;
    this.v = 0;
    this.actif = -1;
    this.annonce = depart;
    this.visible = false;
    this.raf = 0;

    this.refs = this.items.map((li) => ({
      li,
      corps: li.querySelector('[data-mnp-corps]'),
      visuel: li.querySelector('[data-mnp-visuel]'),
      nom: li.querySelector('[data-mnp-nom]'),
      fiche: li.querySelector('[data-mnp-fiche]'),
      focusables: Array.from(li.querySelectorAll('[data-mnp-fiche] a[href], [data-mnp-fiche] button')),
      titre: li.dataset.titre || '',
      memo: {},
    }));

    this.precedent = this.querySelector('[data-mnp-prec]');
    this.suivant = this.querySelector('[data-mnp-suiv]');
    this.points = Array.from(this.querySelectorAll('[data-mnp-point]'));
    this.indic = this.querySelector('[data-mnp-indic]');
    this.curseur = this.querySelector('[data-mnp-curseur]');
    this.statut = this.querySelector('[data-mnp-statut]');
    this.plans = Array.from(this.querySelectorAll('[data-mnp-plan]')).map((el) => ({
      el,
      force: parseFloat(el.dataset.mnpPlan) || 0,
    }));

    this.tic = this.tic.bind(this);

    this.mesurer();
    this.peindre();
    this.majActif();
    this.classList.add('mnp--pret');
    if (this.calme || this.design) this.classList.add('mnp--calme');

    if (this.n > 1) this.brancherNavigation();
    this.brancherVisibilite();
    this.brancherParallaxe();
    this.brancherEditeur();
  }

  arreter() {
    (this.nettoyeurs || []).forEach((f) => f());
    this.nettoyeurs = [];
    cancelAnimationFrame(this.raf);
    this.raf = 0;
    if (this.parallaxe) cancelAnimationFrame(this.parallaxe.raf);
    clearTimeout(this.minuteur);
  }

  ecouter(el, type, fn, options) {
    if (!el) return;
    el.addEventListener(type, fn, options);
    this.nettoyeurs.push(() => el.removeEventListener(type, fn, options));
  }

  /* — Mesures : lues au démarrage et à chaque redimensionnement — */

  mesurer() {
    const cs = getComputedStyle(this);
    const lire = (nom, defaut) => {
      const v = parseFloat(cs.getPropertyValue(nom));
      return Number.isFinite(v) ? v : defaut;
    };
    const espacement = lire('--mnp-esp', 1);
    const perspective = lire('--mnp-rot', 1);
    const largeur = this.scene.clientWidth || 1;
    const sonde = this.querySelector('[data-mnp-sonde]');
    const hauteurBoite = sonde ? sonde.offsetHeight : 300;

    this.g = {
      x: [0, lire('--mnp-x1', 0.2), lire('--mnp-x2', 0.32), lire('--mnp-x3', 0.42)].map((v) => v * espacement * largeur),
      s: [1, lire('--mnp-s1', 0.8), lire('--mnp-s2', 0.64), lire('--mnp-s3', 0.52)],
      r: [0, lire('--mnp-r1', 24), lire('--mnp-r2', 34), lire('--mnp-r3', 40)].map((v) => v * perspective),
      y: [0, lire('--mnp-y1', -0.07), lire('--mnp-y2', -0.12), lire('--mnp-y3', -0.15)].map((v) => v * hauteurBoite),
      pers: lire('--mnp-pers', 1200),
      visibles: lire('--mnp-visibles', 2),
      fondu: Math.max(0.05, lire('--mnp-fondu', 0.6)),
      voisins: lire('--mnp-legendes-voisines', 1),
      // Au-delà de cet écart, les noms s'effacent : ils se chevaucheraient.
      portee: lire('--mnp-noms-portee', 1.25),
    };
    // Un cran de glissement = la distance entre le centre et un voisin.
    this.pas = Math.max(60, this.g.x[1]);

    // Les positions clés sont pensées pour des tableaux verticaux. Un
    // tableau en paysage est bien plus large : on mesure ce qu'il a en trop
    // par rapport à un 3:4 de même hauteur, et l'écart avec ses voisins
    // grandit d'autant (voir decalages). Les coussins ne sont pas concernés.
    const adapter = this.classList.contains('mnp--tableaux');
    this.refs.forEach((r) => {
      r.exces = 0;
      if (!adapter || !r.visuel || !r.corps) return;
      const cadre = r.visuel.firstElementChild;
      const image = cadre && cadre.querySelector('img, svg');
      if (!image) return;
      const largeur = cadre.offsetWidth;
      const hauteur = r.corps.offsetHeight;
      const marge = (largeur - image.offsetWidth) / 2;
      const vertical = (hauteur - 2 * marge) * 0.75 + 2 * marge;
      r.exces = Math.max(0, largeur - vertical);
    });
    this.adapte = this.refs.some((r) => r.exces > 0);

    this.largeurIndic = this.indic ? this.indic.clientWidth : 0;
    if (this.indic && this.points.length > 1) {
      // Les repères ne deviennent cliquables que si leur espacement laisse
      // une vraie cible tactile ; sinon flèches et glissement suffisent.
      const ecart = (this.largeurIndic * 0.76) / (this.points.length - 1);
      const passif = ecart < 40;
      this.indic.classList.toggle('mnp__indic--passif', passif);
      this.points.forEach((b) => {
        b.tabIndex = passif ? -1 : 0;
        if (passif) b.setAttribute('aria-hidden', 'true');
        else b.removeAttribute('aria-hidden');
      });
    }
  }

  /* — Rendu d'une image — */

  /**
   * Décalage horizontal de chaque tableau, pour que les plus larges ne
   * mordent pas sur leurs voisins.
   *
   * Entre deux tableaux qui se suivent, on ajoute la moitié de ce que
   * chacun a en trop, à son échelle du moment. Ces ajouts s'additionnent
   * de proche en proche à partir du centre, qui reste fixe, y compris quand
   * p est entre deux crans : le mouvement reste continu.
   */
  decalages() {
    const { g, n } = this;
    const decal = new Array(n).fill(0);
    if (!this.adapte) return decal;

    const portee = g.visibles + g.fondu + 1;
    const rangs = [];
    for (let i = 0; i < n; i++) {
      let d = i - this.p;
      if (this.boucle) d = modulo(d + n / 2, n) - n / 2;
      if (Math.abs(d) < portee) rangs.push({ i, d });
    }
    rangs.sort((a, b) => a.d - b.d);

    const echelle = (d) => Math.max(0.3, palier(g.s, Math.min(Math.abs(d), 3)));
    const cumul = [0];
    for (let k = 1; k < rangs.length; k++) {
      const a = rangs[k - 1];
      const b = rangs[k];
      cumul.push(cumul[k - 1] + (echelle(a.d) * this.refs[a.i].exces + echelle(b.d) * this.refs[b.i].exces) / 2);
    }

    // Le centre de la scène se trouve entre le dernier tableau à gauche
    // (d ≤ 0) et le suivant : on y place le zéro.
    let gauche = -1;
    rangs.forEach((r, k) => { if (r.d <= 0) gauche = k; });
    let zero = 0;
    if (gauche >= 0) {
      zero = cumul[gauche];
      if (gauche + 1 < rangs.length) zero += -rangs[gauche].d * (cumul[gauche + 1] - cumul[gauche]);
    }
    rangs.forEach((r, k) => { decal[r.i] = cumul[k] - zero; });
    return decal;
  }

  peindre() {
    const { g, n } = this;
    const limite = g.visibles + g.fondu;
    const decal = this.decalages();

    for (let i = 0; i < n; i++) {
      const r = this.refs[i];
      const m = r.memo;
      let d = i - this.p;
      if (this.boucle) d = modulo(d + n / 2, n) - n / 2;
      const a = Math.abs(d);

      if (a >= limite) {
        if (m.cache !== true) {
          r.li.style.visibility = 'hidden';
          r.li.style.opacity = '0';
          m.o = '0';
          m.cache = true;
        }
        continue;
      }
      if (m.cache !== false) {
        r.li.style.visibility = 'visible';
        m.cache = false;
      }

      const signe = d < 0 ? -1 : 1;
      const x = signe * palier(g.x, a) + decal[i];
      const y = palier(g.y, Math.min(a, 3));
      const echelle = Math.max(0.3, palier(g.s, Math.min(a, 3)));
      const rotation = signe * palier(g.r, Math.min(a, 3));
      const opacite = a <= g.visibles ? 1 : borne(1 - (a - g.visibles) / g.fondu, 0, 1);

      const t = `translate3d(${x.toFixed(1)}px,${y.toFixed(1)}px,0)`;
      if (m.t !== t) { r.li.style.transform = t; m.t = t; }

      const z = String(1000 - Math.round(a * 100));
      if (m.z !== z) { r.li.style.zIndex = z; m.z = z; }

      const o = opacite.toFixed(3);
      if (m.o !== o) { r.li.style.opacity = o; m.o = o; }

      if (r.corps) {
        const s = `scale(${echelle.toFixed(4)})`;
        if (m.s !== s) { r.corps.style.transform = s; m.s = s; }
      }
      if (r.visuel) {
        const v = `perspective(${g.pers}px) rotateY(${rotation.toFixed(2)}deg)`;
        if (m.v !== v) { r.visuel.style.transform = v; m.v = v; }
      }

      // Le produit central montre sa fiche ; les voisins, leur seul nom.
      const presence = borne(1 - a * 2.2, 0, 1);
      if (r.fiche) {
        const f = presence.toFixed(3);
        if (m.f !== f) { r.fiche.style.opacity = f; m.f = f; }
      }
      if (r.nom) {
        const loin = borne((g.portee + 0.5 - a) / 0.5, 0, 1);
        const nm = ((1 - presence) * g.voisins * loin).toFixed(3);
        if (m.nm !== nm) { r.nom.style.opacity = nm; m.nm = nm; }
      }
    }

    if (this.curseur && n > 1) {
      let t;
      if (!this.boucle) {
        t = repere(borne(this.p, 0, n - 1), n);
      } else {
        const q = modulo(this.p, n);
        t = q <= n - 1 ? repere(q, n) : repere(n - 1, n) + (repere(0, n) - repere(n - 1, n)) * (q - (n - 1));
      }
      const c = `translate3d(${(t * this.largeurIndic).toFixed(1)}px,${(22 - 48 * t * (1 - t)).toFixed(1)}px,0)`;
      if (this.memoCurseur !== c) { this.curseur.style.transform = c; this.memoCurseur = c; }
    }

    const actif = this.boucle ? modulo(Math.round(this.p), n) : borne(Math.round(this.p), 0, n - 1);
    if (actif !== this.actif) {
      this.actif = actif;
      this.majActif();
    }
  }

  majActif() {
    const a = this.actif < 0 ? 0 : this.actif;
    this.refs.forEach((r, i) => {
      const est = i === a;
      r.li.classList.toggle('est-actif', est);
      if (!est && r.li.contains(document.activeElement)) {
        // Le focus ne reste pas sur un produit qui quitte le centre.
        this.scene.focus({ preventScroll: true });
      }
      r.focusables.forEach((el) => {
        if (est) el.removeAttribute('tabindex');
        else el.tabIndex = -1;
      });
    });
    this.points.forEach((b, i) => b.setAttribute('aria-current', i === a ? 'true' : 'false'));
    if (!this.boucle) {
      if (this.precedent) this.precedent.setAttribute('aria-disabled', a <= 0 ? 'true' : 'false');
      if (this.suivant) this.suivant.setAttribute('aria-disabled', a >= this.n - 1 ? 'true' : 'false');
    }
    // Signal pour les sections qui accompagnent le présentoir (fiche du
    // tableau sélectionné sur la page Tableaux). Sans écouteur, il est sans effet.
    this.dispatchEvent(new CustomEvent('menara:presentoir-actif', { bubbles: true, detail: { index: a, total: this.n } }));
  }

  /* — Mouvement — */

  lancer() {
    if (this.calme) {
      this.p = this.cible;
      this.v = 0;
      this.peindre();
      this.repos();
      return;
    }
    if (!this.raf) {
      this.tPrecedent = performance.now();
      this.raf = requestAnimationFrame(this.tic);
    }
  }

  planifier() {
    if (!this.raf) {
      this.tPrecedent = performance.now();
      this.raf = requestAnimationFrame(this.tic);
    }
  }

  tic(t) {
    this.raf = 0;
    const dt = Math.min(0.05, Math.max(0, (t - this.tPrecedent) / 1000));
    this.tPrecedent = t;

    if (!this.glisse) {
      // Ressort critique, intégré exactement : indépendant de la cadence.
      const w = this.omega;
      const ecart = this.p - this.cible;
      const e = Math.exp(-w * dt);
      const k = (this.v + w * ecart) * dt;
      this.v = (this.v - w * k) * e;
      const reste = (ecart + k) * e;
      this.p = this.cible + reste;
      if (Math.abs(reste) < 5e-4 && Math.abs(this.v) < 5e-3) {
        this.p = this.cible;
        this.v = 0;
        this.peindre();
        this.repos();
        return;
      }
    }

    this.peindre();
    if (!this.glisse) this.raf = requestAnimationFrame(this.tic);
  }

  repos() {
    if (this.boucle) {
      const c = modulo(this.cible, this.n);
      if (c !== this.cible) {
        this.p = c;
        this.cible = c;
      }
    }
    if (this.statut && this.interagi && this.annonce !== this.actif) {
      const r = this.refs[this.actif];
      this.statut.textContent = `${r.titre}, ${this.actif + 1} sur ${this.n}`;
      this.annonce = this.actif;
    }
    this.planifierAuto();
  }

  decaler(delta) {
    let c = Math.round(this.cible) + delta;
    if (!this.boucle) c = borne(c, 0, this.n - 1);
    this.cible = c;
    this.lancer();
  }

  aller(i) {
    if (this.boucle) {
      const base = Math.round(this.cible);
      let delta = modulo(i - base, this.n);
      if (delta > this.n / 2) delta -= this.n;
      this.cible = base + delta;
    } else {
      this.cible = borne(i, 0, this.n - 1);
    }
    this.lancer();
  }

  interaction() {
    this.interagi = true;
    this.pauseJusqua = Date.now() + 9000;
    this.planifierAuto();
  }

  /* — Commandes : flèches, repères, clavier, glissement — */

  brancherNavigation() {
    this.ecouter(this.precedent, 'click', () => { this.interaction(); this.decaler(-1); });
    this.ecouter(this.suivant, 'click', () => { this.interaction(); this.decaler(1); });
    this.points.forEach((b, i) => this.ecouter(b, 'click', () => { this.interaction(); this.aller(i); }));
    this.ecouter(this, 'keydown', (e) => this.clavier(e));

    this.ecouter(this.scene, 'pointerdown', (e) => this.appui(e));
    this.ecouter(this.scene, 'pointermove', (e) => this.glisser(e));
    this.ecouter(this.scene, 'pointerup', (e) => this.relacher(e));
    this.ecouter(this.scene, 'pointercancel', (e) => this.relacher(e));
    this.ecouter(this.scene, 'click', (e) => this.clic(e), true);
    this.ecouter(this.scene, 'dragstart', (e) => e.preventDefault());

    if (this.auto) {
      this.ecouter(this, 'pointerenter', () => { this.survol = true; clearTimeout(this.minuteur); });
      this.ecouter(this, 'pointerleave', () => { this.survol = false; this.planifierAuto(); });
      this.ecouter(this, 'focusin', () => { this.focusDedans = true; clearTimeout(this.minuteur); });
      this.ecouter(this, 'focusout', (e) => {
        if (this.contains(e.relatedTarget)) return;
        this.focusDedans = false;
        this.planifierAuto();
      });
    }
  }

  clavier(e) {
    if (e.defaultPrevented || e.altKey || e.ctrlKey || e.metaKey) return;
    if (e.target.closest('input, textarea, select')) return;
    let traite = true;
    switch (e.key) {
      case 'ArrowRight': this.decaler(1); break;
      case 'ArrowLeft': this.decaler(-1); break;
      case 'Home': this.aller(0); break;
      case 'End': this.aller(this.n - 1); break;
      default: traite = false;
    }
    if (traite) {
      e.preventDefault();
      this.interaction();
    }
  }

  appui(e) {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    this.pointeur = { id: e.pointerId, x0: e.clientX, y0: e.clientY, p0: this.p, echantillons: [] };
  }

  glisser(e) {
    const ptr = this.pointeur;
    if (!ptr || e.pointerId !== ptr.id) return;

    if (!this.glisse) {
      const dx = e.clientX - ptr.x0;
      const dy = e.clientY - ptr.y0;
      if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
      // Geste plutôt vertical : c'est la page qui défile, on s'efface.
      if (Math.abs(dy) > Math.abs(dx)) { this.pointeur = null; return; }
      this.glisse = true;
      try { this.scene.setPointerCapture(e.pointerId); } catch (_) { /* pointeur déjà relâché */ }
      this.classList.add('est-glisse');
      ptr.x0 = e.clientX;
      ptr.p0 = this.p;
      this.v = 0;
      this.interaction();
    }

    let p = ptr.p0 - (e.clientX - ptr.x0) / this.pas;
    if (!this.boucle) {
      // Au-delà des extrémités, la résistance dit qu'il n'y a plus rien.
      const max = this.n - 1;
      if (p < 0) p *= 0.35;
      else if (p > max) p = max + (p - max) * 0.35;
    }
    this.p = p;
    ptr.echantillons.push([e.timeStamp, p]);
    while (ptr.echantillons.length > 2 && e.timeStamp - ptr.echantillons[0][0] > 120) ptr.echantillons.shift();
    this.planifier();
  }

  relacher(e) {
    const ptr = this.pointeur;
    if (!ptr || (e && e.pointerId !== ptr.id)) return;
    this.pointeur = null;
    if (!this.glisse) return;

    this.glisse = false;
    this.classList.remove('est-glisse');

    let v = 0;
    const ech = ptr.echantillons;
    if (ech.length >= 2) {
      const [t0, p0] = ech[0];
      const [t1, p1] = ech[ech.length - 1];
      if (t1 > t0) v = (p1 - p0) / ((t1 - t0) / 1000);
    }
    v = borne(v, -12, 12);

    // Projection du geste, limitée à trois crans : on reste maître de ce
    // qu'on voit défiler.
    const origine = Math.round(ptr.p0);
    let c = borne(Math.round(this.p + v * 0.16), origine - 3, origine + 3);
    if (!this.boucle) c = borne(c, 0, this.n - 1);
    this.cible = c;
    this.v = v;

    // Le clic qui suit un glissement ne doit ouvrir aucune fiche.
    this.clicBloque = true;
    setTimeout(() => { this.clicBloque = false; }, 60);
    this.lancer();
  }

  /**
   * Ouvre la fiche du produit du centre quand on clique sur le produit
   * lui-même. Le lien est celui que porte déjà sa légende : une pièce sans
   * lien — annoncée « bientôt », ou un présentoir qui n'en pose pas — ne
   * mène nulle part, et le clic ne fait rien.
   */
  ouvrirFiche(li, e) {
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    if (window.Shopify && window.Shopify.designMode) return;
    const lien = li.querySelector('a[href]');
    if (!lien) return;
    e.preventDefault();
    window.location.href = lien.href;
  }

  clic(e) {
    if (this.clicBloque) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    const li = e.target.closest('[data-mnp-item]');
    if (!li) return;
    const i = this.items.indexOf(li);
    if (i < 0) return;
    // Le bouton « Découvrir » ou « + » ouvre la fiche tout seul.
    if (e.target.closest('a[href], button')) return;
    // Le produit du centre ouvre sa fiche ; les autres viennent au centre.
    if (i === this.actif) {
      this.ouvrirFiche(li, e);
      return;
    }
    e.preventDefault();
    this.interaction();
    this.aller(i);
  }

  /* — Visibilité, apparition, redimensionnement — */

  brancherVisibilite() {
    if (!('IntersectionObserver' in window)) {
      this.visible = true;
      this.classList.add('est-entre');
      return;
    }

    const presence = new IntersectionObserver(([entree]) => {
      this.visible = entree.isIntersecting;
      if (this.visible) this.planifierAuto();
      else this.suspendre();
    }, { rootMargin: '160px 0px' });
    presence.observe(this);

    const apparition = new IntersectionObserver((entrees) => {
      if (!entrees.some((x) => x.isIntersecting)) return;
      this.classList.add('est-entre');
      apparition.disconnect();
    }, { threshold: 0.16 });
    if (this.calme || this.design) this.classList.add('est-entre');
    else apparition.observe(this);

    let rafMesure = 0;
    const redimension = new ResizeObserver(() => {
      cancelAnimationFrame(rafMesure);
      rafMesure = requestAnimationFrame(() => {
        this.mesurer();
        this.refs.forEach((r) => { r.memo = {}; });
        this.memoCurseur = '';
        this.peindre();
      });
    });
    redimension.observe(this.scene);

    this.nettoyeurs.push(() => {
      presence.disconnect();
      apparition.disconnect();
      redimension.disconnect();
      cancelAnimationFrame(rafMesure);
    });
  }

  suspendre() {
    clearTimeout(this.minuteur);
    if (this.parallaxe && this.parallaxe.raf) {
      cancelAnimationFrame(this.parallaxe.raf);
      this.parallaxe.raf = 0;
    }
  }

  planifierAuto() {
    clearTimeout(this.minuteur);
    if (!this.auto || this.calme || !this.visible || this.survol || this.focusDedans || this.glisse || this.n < 2) return;
    const attente = Math.max(this.delaiAuto, (this.pauseJusqua || 0) - Date.now());
    this.minuteur = setTimeout(() => {
      if (!this.boucle && Math.round(this.cible) >= this.n - 1) this.aller(0);
      else this.decaler(1);
    }, attente);
  }

  /* — Parallaxe du décor : ordinateur uniquement, jamais en mouvement réduit — */

  brancherParallaxe() {
    if (!this.plans.length || this.calme || this.design || !POINTEUR_FIN.matches) return;
    const px = (this.parallaxe = { x: 0, y: 0, cx: 0, cy: 0, raf: 0 });

    const pas = () => {
      px.raf = 0;
      px.x += (px.cx - px.x) * 0.07;
      px.y += (px.cy - px.y) * 0.07;
      for (const plan of this.plans) {
        plan.el.style.transform = `translate3d(${(px.x * plan.force).toFixed(2)}px,${(px.y * plan.force * 0.6).toFixed(2)}px,0)`;
      }
      if (Math.abs(px.cx - px.x) > 0.002 || Math.abs(px.cy - px.y) > 0.002) px.raf = requestAnimationFrame(pas);
    };
    const relancer = () => { if (!px.raf && this.visible) px.raf = requestAnimationFrame(pas); };

    // Aucune mesure ici : la position rapportée à la fenêtre suffit.
    this.ecouter(this, 'pointermove', (e) => {
      if (e.pointerType !== 'mouse') return;
      px.cx = (e.clientX / window.innerWidth) * 2 - 1;
      px.cy = (e.clientY / window.innerHeight) * 2 - 1;
      relancer();
    }, { passive: true });
    this.ecouter(this, 'pointerleave', () => { px.cx = 0; px.cy = 0; relancer(); });
  }

  /* — Éditeur de thème : le bloc sélectionné vient au centre — */

  brancherEditeur() {
    if (!this.design) return;
    const choisir = (e) => {
      if (!this.contains(e.target)) return;
      const li = e.target.closest ? e.target.closest('[data-mnp-item]') : null;
      const i = this.items.indexOf(li);
      if (i >= 0) this.aller(i);
    };
    document.addEventListener('shopify:block:select', choisir);
    this.nettoyeurs.push(() => document.removeEventListener('shopify:block:select', choisir));
  }
}

if (!customElements.get('menara-presentoir')) {
  customElements.define('menara-presentoir', MenaraPresentoir);
}
